import {
  Badge,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PaneListEmptyState,
  PaneListError,
  PaneListSkeleton,
  navigateWithThreadPreservation,
} from '@yeshuman/sdk'
import { Briefcase } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import {
  useRecruiterClientJobs,
  useRecruiterClients,
  useSelectRecruiterClient,
} from '../../hooks/use-recruiter-clients'

export function RecruiterClientJobsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const clientSlug = searchParams.get('client')
  const clients = useRecruiterClients()
  const jobs = useRecruiterClientJobs(clientSlug)
  const selectClient = useSelectRecruiterClient()
  const selected = clients.data?.find((client) => client.organisation_slug === clientSlug) ?? null

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title="Jobs"
        description="Work an employer's jobs from here. Upload CVs on the pipeline, including drafts."
        icon={<Briefcase className="h-5 w-5" />}
      />

      {clients.isLoading ? <PaneListSkeleton /> : null}
      {clients.error ? (
        <PaneListError
          message={clients.error instanceof Error ? clients.error.message : 'Failed to load clients'}
        />
      ) : null}

      {!clients.isLoading && !clients.error ? (
        <div className="mb-8 flex flex-wrap gap-2">
          {(clients.data ?? []).map((client) => {
            const active = client.organisation_slug === clientSlug
            return (
              <Button
                key={client.organisation_id}
                type="button"
                variant={active ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setSearchParams({ client: client.organisation_slug })
                  if (!client.granted) {
                    selectClient.mutate(client.organisation_id, {
                      onError: (error) => {
                        toast.error(error instanceof Error ? error.message : 'Could not select client')
                      },
                    })
                  }
                }}
              >
                {client.organisation_name}
                <Badge variant="secondary">{client.granted ? 'Granted' : 'Not granted'}</Badge>
              </Button>
            )
          })}
          {(clients.data ?? []).length === 0 ? (
            <PaneListEmptyState
              icon={<Briefcase aria-hidden />}
              title="No employer jobs yet"
              description="When an employer has a job, it shows up here."
            />
          ) : null}
        </div>
      ) : null}

      {selected ? (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">{selected.organisation_name}</h2>
          {jobs.isLoading ? <PaneListSkeleton /> : null}
          {jobs.error ? (
            <PaneListError
              message={jobs.error instanceof Error ? jobs.error.message : 'Failed to load jobs'}
            />
          ) : null}
          <ul className="divide-y divide-border rounded-md border border-border">
            {(jobs.data ?? []).map((job) => (
              <li key={job.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40"
                  onClick={() =>
                    navigateWithThreadPreservation(
                      navigate,
                      `/recruiter/jobs/${job.id}/applicants`,
                      searchParams,
                    )
                  }
                >
                  <span className="font-medium">{job.title}</span>
                  <Badge variant="outline">{job.publication_status}</Badge>
                </button>
              </li>
            ))}
          </ul>
          {!jobs.isLoading && (jobs.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">This employer has no jobs yet.</p>
          ) : null}
        </section>
      ) : null}
    </PageContainer>
  )
}
